import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '@/utils/api';

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  image?: string;
  parentId?: string;
  order?: number;
  children?: Category[];
}

interface CategorieContextValue {
  categories: Category[];
  loading: boolean;
  error: string | null;
  fetchCategories: () => Promise<Category[]>;
  fetchCategory: (id: string) => Promise<Category | null>;
  createCategory: (data: { name: string; image?: string; icon?: string; parentId?: string }) => Promise<Category | null>;
  updateCategory: (id: string, data: { name?: string; image?: string; icon?: string; parentId?: string }) => Promise<Category | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  clearError: () => void;
}

const CategorieContext = createContext<CategorieContextValue | null>(null);

export function useCategorie(): CategorieContextValue {
  const ctx = useContext(CategorieContext);
  if (!ctx) throw new Error('useCategorie must be used within CategorieProvider');
  return ctx;
}

export function CategorieProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const handleError = useCallback((err: unknown, fallback: string) => {
    const message = err instanceof Error ? err.message : fallback;
    setError(message);
    return null;
  }, []);

  const fetchCategories = useCallback(async (): Promise<Category[]> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Category[]>('/categories');
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
      return list;
    } catch (err) {
      handleError(err, 'Impossible de charger les catégories.');
      return [];
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const fetchCategory = useCallback(async (id: string): Promise<Category | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Category>(`/categories/${id}`);
      return data;
    } catch (err) {
      return handleError(err, 'Impossible de charger la catégorie.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const createCategory = useCallback(async (data: { name: string; image?: string; icon?: string; parentId?: string }): Promise<Category | null> => {
    setLoading(true);
    setError(null);
    try {
      const created = await api.post<Category>('/categories', data);
      setCategories((prev) => [...prev, created]);
      return created;
    } catch (err) {
      return handleError(err, 'Erreur lors de la création de la catégorie.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const updateCategory = useCallback(async (id: string, data: { name?: string; image?: string; icon?: string; parentId?: string }): Promise<Category | null> => {
    setLoading(true);
    setError(null);
    try {
      const updated = await api.patch<Category>(`/categories/${id}`, data);
      setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return updated;
    } catch (err) {
      return handleError(err, 'Erreur lors de la mise à jour de la catégorie.');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const deleteCategory = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/categories/${id}`);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (err) {
      handleError(err, 'Impossible de supprimer la catégorie.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const value: CategorieContextValue = {
    categories,
    loading,
    error,
    fetchCategories,
    fetchCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    clearError,
  };

  return <CategorieContext.Provider value={value}>{children}</CategorieContext.Provider>;
}
